<script setup lang="ts">
import MyBigWinsHeader from '~/components/account/my-big-wins/my-big-wins-header.vue';
import { useWinnersData } from '~/composables/useStates/useWinnersData';
import GenericError from '~/components/user-interface/generic-error.vue';
import Pagination from '~/components/design-system/pagination.vue';

const { $trackEvent } = useNuxtApp();
const {
  bigWinsLoading,
  loadMyBigWins,
  myBigWins,
  totalBigWins,
  bigWinsSortField,
  bigWinsSortOrder,
} = useWinnersData();
const { md, lgOrMore } = useBreakpoint();
const auth = useAuthStore();
const viewport = useViewport();

const limit = computed(() => (viewport.isLessThan('tablet') ? 5 : 8));

const currentPage = ref<number>(1);
const showingStart = ref<number>(0);
const showingEnd = ref<number>(0);

const totalPages = computed(() =>
  !!totalBigWins.value ? Math.ceil(totalBigWins.value / limit.value) : 0,
);

const paginate = (type: 'next' | 'previous' | 'select', pageNo?: number) => {
  const doPagination = () => {
    loadMyBigWins({
      skip: currentPage.value * limit.value,
      limit: limit.value,
      sortField: bigWinsSortField.value,
      sortOrder: bigWinsSortOrder.value,
    });
    updateShowingRange();
    $trackEvent('WinnersEvent', 'WINNERS_PAGINATE_WINS');
  };

  switch (type) {
    case 'next':
      if (currentPage.value < totalPages.value) {
        currentPage.value++;
        doPagination();
      }
      break;
    case 'previous':
      if (currentPage.value > 1) {
        currentPage.value--;
        doPagination();
      }
      break;
    case 'select':
      currentPage.value = pageNo;
      doPagination();
      break;
    default:
      break;
  }
};

const updateShowingRange = () => {
  showingStart.value =
    currentPage.value === 1 ? 1 : (currentPage.value - 1) * limit.value + 1;
  showingEnd.value = Math.min(
    currentPage.value * limit.value,
    totalBigWins.value,
  );
};

const hasBigWins = computed(() => !!myBigWins && myBigWins.value?.length > 0);

const showHeader = computed(() => hasBigWins.value || !!bigWinsLoading.value);

onMounted(() =>
  loadMyBigWins({ skip: 0, limit: limit.value, resetGameNames: true }).then(
    () => updateShowingRange(),
  ),
);

provide<Ref<number>>('myBigWinsLimit', limit);
</script>

<template>
  <section class="md:p-2">
    <MyBigWinsHeader v-if="showHeader" />
    <LazyUserInterfaceGenericLoader
      v-if="bigWinsLoading"
      class="my-auto"
      standalone
    />
    <div
      v-if="hasBigWins && !bigWinsLoading"
      class="w-full flex flex-col items-center justify-center"
    >
      <div class="w-full md:w-fit flex flex-col px-2 pb-2">
        <div
          class="grid grid-cols-1 md:grid-cols-2 gap-2 w-full md:w-fit mx-auto bg-layer p-2 my-2 rounded-lg"
          :class="{
            'grid-cols-2': md,
          }"
        >
          <LazyWinnersMyBigWinsCard
            v-for="(record, index) in myBigWins"
            :key="index"
            :index="index"
            :win-record="record"
            :stretch="!lgOrMore"
            bg-mode="lighter"
            @click="auth.toggleAccountModal(false)"
          />
        </div>
      </div>
      <div
        v-if="totalBigWins > limit"
        class="pagination flex place-content-center justify-center py-3 sticky bottom-0 rounded-b-lg"
      >
        <Pagination
          :current-page="currentPage"
          :total-pages="totalPages"
          :showing-start="showingStart"
          :showing-end="showingEnd"
          :total-results="totalBigWins"
          @next="paginate('next')"
          @previous="paginate('previous')"
          @select="paginate('select', $event)"
        />
      </div>
    </div>
    <GenericError
      v-if="!bigWinsLoading && myBigWins?.length === 0"
      :scroll-to-view="false"
      state="warn"
      class="m-4"
    >
      {{ $t('my-big-wins-list-empty') }}
      <NuxtLink
        to="/"
        class="underline"
        @click="auth.toggleAccountModal(false)"
      >
        {{ $t('bet-now') }}
      </NuxtLink>
    </GenericError>
  </section>
</template>
